import Decimal from 'decimal.js';
import { compact, first, isEmpty, partition } from 'lodash';
import { formatCompactValue } from '@/modules/estateWaterfall/components/EstateWaterfallSummaryTiles/EstateWaterfallSummaryTiles.utils';
import { BalanceSheetRowType } from '@/types/schema';
import { formatCurrency, formatCurrencyNoDecimalsAccounting } from '@/utils/formatting/currency';
import { getPulidKind, PulidKind } from '@/utils/pulid';
import { ENTITY_VALUE_COLUMN_ID, ESTATE_TOTAL_COLUMN_ID, getIsTotalColumn, GRAND_TOTAL_COLUMN_ID, IN_ESTATE_TOTAL_COLUMN_ID, OUT_OF_ESTATE_TOTAL_COLUMN_ID } from './BalanceSheetTable.columns';
import { RowData, RowValue } from './BalanceSheetTable.constants';
import { BALANCE_SHEET_COLUMN_GAP_WIDTH } from './BalanceSheetTable.layout';
import { BalanceSheetCellPosition, BalanceSheetCellStyle, BalanceSheetSummaryVariant } from './BalanceSheetTable.types';
import { BalanceSheetTable_BalanceSheetColumnFragment, BalanceSheetTable_BalanceSheetFragment, BalanceSheetTable_BalanceSheetRowFragment, BalanceSheetTable_BalanceSheetRowValueFragment } from './graphql/BalanceSheetTable.fragments.generated';
function buildBalanceSheetRow(
// we do this instead of doing the framgnet directly because there's a single level of a recursive call to children
// and the fragment can't recurse upon itself
row: BalanceSheetTable_BalanceSheetFragment['rows'][number], parentPath: string[]): RowData {
  return {
    id: row.ID,
    name: {
      lineOne: row.display,
      lineTwo: row.description || undefined
    },
    path: [...parentPath, row.ID],
    hasChildren: !isEmpty(row.children),
    values: getNormalizedValues(row.values, row.associatedNodes?.some(node => node?.__typename === 'InsurancePolicy')),
    variant: row.type,
    ...getAssociatedNodeIds(row)
  };
}

/**
 * @description Extracts the entity and liability IDs from the associated nodes of a balance sheet row. This is used to power the
 * popover that lets you quickly access the details of an entity or liability from the balance sheet.
 */
function getAssociatedNodeIds(row: BalanceSheetTable_BalanceSheetRowFragment): {
  entityIds?: string[];
  liabilityId?: string;
} {
  if (!row.associatedNodes) return {};

  // make ts happy
  const nodes = compact(row.associatedNodes.map(n => {
    if (!n) return null;
    if (!('id' in n)) return null;
    return n;
  }));
  const [entityNodes, liabilityNodes] = partition(nodes, n => {
    if (!('id' in n)) return false;
    return getPulidKind(n.id) === PulidKind.Entity;
  });
  return {
    entityIds: entityNodes.map(n => n.id),
    liabilityId: first(liabilityNodes.map(n => n.id))
  };
}

/**
 * Subtext shown for an insurance policy whose kind cannot carry a cash value
 * (e.g. term life) when the death benefit is the primary value (T1-6546). A
 * permanent policy with no cash value entered instead shows "$0 cash value".
 */
const CASH_VALUE_UNSUPPORTED_PLACEHOLDER = '--';
export function getNormalizedValues(values: BalanceSheetTable_BalanceSheetRowValueFragment[], isInsurancePolicy = false): Record<string, RowValue> {
  const normalizedValues: Record<string, RowValue> = {};
  values.forEach(value => {
    let description: string | undefined = undefined;
    if (isInsurancePolicy && value.columnID === ENTITY_VALUE_COLUMN_ID) {
      // Entity reports count cash value only. Death benefit is in Details,
      // so this line labels the primary amount instead of a secondary amount.
      // Household reports keep their configured primary and secondary values.
      if (value.value && !value.value.isZero()) description = 'cash value';
    } else if (value.supportsCashValue === false) {
      description = CASH_VALUE_UNSUPPORTED_PLACEHOLDER;
    } else if (value.secondaryValue && value.secondaryValueDescription) {
      description = `${formatCompactValue(value.secondaryValue)} ${value.secondaryValueDescription}`;
    }
    normalizedValues[value.columnID] = {
      value: value.value,
      description,
      secondaryValue: value.secondaryValue ?? null,
      secondaryValueDescription: value.secondaryValueDescription ?? null,
      supportsCashValue: value.supportsCashValue ?? null
    };
  });
  return normalizedValues;
}
export function mapDataToRows(balanceSheet: BalanceSheetTable_BalanceSheetFragment | null, {
  isViewOnly,
  includeChildren = false
}: {
  isViewOnly: boolean;
  includeChildren?: boolean;
}): RowData[] {
  const rows: RowData[] = [];
  function recurseRow(category: BalanceSheetTable_BalanceSheetFragment['rows'][number], parentPath: string[]) {
    const categoryRow = buildBalanceSheetRow(category, parentPath);
    rows.push(categoryRow);
    if (category.children && (!isViewOnly || includeChildren)) {
      category.children.forEach(child => {
        recurseRow(child, categoryRow.path);
      });
    }
  }
  (balanceSheet?.rows ?? []).forEach(row => {
    recurseRow(row, []);
  });
  return rows;
}

// ============================================================================
// Constants
// ============================================================================

export interface ColumnSizeConfig {
  minWidth: number;
  maxWidth?: number;
}

/** Column sizing for app (in-app) view */
export const APP_ASSET_COLUMN: ColumnSizeConfig = {
  minWidth: 220,
  maxWidth: 300
};
export const APP_DATA_COLUMN: ColumnSizeConfig = {
  minWidth: 150,
  maxWidth: 600
};

/** Column sizing for presentation view */
export const PRESENTATION_ASSET_COLUMN: ColumnSizeConfig = {
  minWidth: 200,
  maxWidth: 275
};
export const PRESENTATION_DATA_COLUMN: ColumnSizeConfig = {
  minWidth: 125
};

/** A single value column leaves the remaining width for holding names and dates. */
export const ENTITY_ASSET_COLUMN: ColumnSizeConfig = {
  minWidth: 200
};
export const ENTITY_DATA_COLUMN: ColumnSizeConfig = {
  minWidth: 200,
  maxWidth: 200
};

/** Column sizing for export view — fixed widths so the grid sizes
 *  intrinsically (total width = sum of columns) without depending on
 *  a container width. */
export const EXPORT_ASSET_COLUMN: ColumnSizeConfig = {
  minWidth: 250,
  maxWidth: 250
};
export const EXPORT_DATA_COLUMN: ColumnSizeConfig = {
  minWidth: 130,
  maxWidth: 130
};

/**
 * Calculate the maximum number of data columns that can fit within the
 * given container width. Accounts for the asset name column and the gap
 * between in-estate and out-of-estate groups.
 *
 * Returns `Infinity` when `containerWidth` is 0 (not yet measured) so the
 * table renders all columns until a real measurement arrives.
 */
export function calculateMaxVisibleColumns(containerWidth: number, assetMinWidth: number, dataMinWidth: number, columnGapWidth: number = BALANCE_SHEET_COLUMN_GAP_WIDTH): number {
  if (containerWidth <= 0 || dataMinWidth <= 0) return Infinity;
  const availableForData = containerWidth - assetMinWidth - columnGapWidth;
  return Math.max(0, Math.floor(availableForData / dataMinWidth));
}

/** Placeholder for cells with no value. Total net worth row uses blank instead. */
const EMPTY_CELL_PLACEHOLDER = '--';

// ============================================================================
// Cell helpers
// ============================================================================

export function getCellStyle(row: RowData, isLastChild: boolean): BalanceSheetCellStyle {
  const variant = row.displayVariant ?? row.variant;
  if (variant === 'SUBTOTALS') return 'subtotals-thick';
  switch (variant) {
    case BalanceSheetRowType.NetWorth:
      return 'totals';
    case BalanceSheetRowType.Total:
      return 'section-totals';
    case BalanceSheetRowType.Category:
      return 'default-primary';
    case BalanceSheetRowType.LineItem:
      return isLastChild ? 'section-end-secondary' : 'default-secondary';
    default:
      return 'default-secondary';
  }
}
export function getCellPosition(column: BalanceSheetTable_BalanceSheetColumnFragment, columnsInGroup: BalanceSheetTable_BalanceSheetColumnFragment[]): BalanceSheetCellPosition {
  if (getIsTotalColumn(column)) {
    return 'summary';
  }

  // The last non-total column in a group gets 'end' position
  const nonTotalColumns = columnsInGroup.filter(c => !getIsTotalColumn(c));
  const lastNonTotal = nonTotalColumns[nonTotalColumns.length - 1];
  if (lastNonTotal && lastNonTotal.ID === column.ID) {
    return 'end';
  }
  return 'middle';
}
export function getSummaryVariant(row: RowData): BalanceSheetSummaryVariant | undefined {
  const variant = row.displayVariant ?? row.variant;
  if (variant === 'SUBTOTALS') return 'strong';
  if (variant === BalanceSheetRowType.NetWorth) return 'blank';
  return undefined;
}
export function formatCellValue(row: RowData, columnId: string): string {
  const variant = row.displayVariant ?? row.variant;
  // The Total net worth row shows blank for empty cells; all others show "--"
  const isTotalNetWorthRow = variant === BalanceSheetRowType.NetWorth;
  const emptyValue = isTotalNetWorthRow ? '' : EMPTY_CELL_PLACEHOLDER;
  const rowValue = row.values[columnId];
  if (!rowValue) return emptyValue;
  const showZeroValueRow = row.variant === BalanceSheetRowType.Total || row.variant === BalanceSheetRowType.NetWorth;
  const showZeroValueColumn = columnId === IN_ESTATE_TOTAL_COLUMN_ID || columnId === OUT_OF_ESTATE_TOTAL_COLUMN_ID || columnId === GRAND_TOTAL_COLUMN_ID || columnId === ESTATE_TOTAL_COLUMN_ID || columnId === ENTITY_VALUE_COLUMN_ID;
  if ((!rowValue.value || rowValue.value.isZero()) && !(showZeroValueRow && showZeroValueColumn)) {
    return emptyValue;
  }
  const value = rowValue.value ?? new Decimal(0);
  return new Decimal(0).lessThan(value) && value.lessThan(new Decimal(1)) ? formatCurrency(value) : formatCurrencyNoDecimalsAccounting(value);
}
export function getCellDescription(row: RowData, columnId: string): string | undefined {
  return row.values[columnId]?.description;
}

// ============================================================================
// Total column recalculation for hidden columns
// ============================================================================

function sumColumnValues(values: Record<string, RowValue>, columnIds: string[]): Decimal {
  let sum = new Decimal(0);
  for (const id of columnIds) {
    const val = values[id]?.value;
    if (val) {
      sum = sum.plus(val);
    }
  }
  return sum;
}

/**
 * Builds a total cell by summing the given columns: both the primary value and
 * the secondary line (e.g. "$X cash value") are recomputed from those columns
 * rather than inherited from the backend total.
 */
function recalculateTotalCell(values: Record<string, RowValue>, columnIds: string[]): RowValue {
  let secondarySum: Decimal | null = null;
  let secondaryLabel: string | null = null;
  let supportsCashValue: boolean | null = null;
  for (const id of columnIds) {
    const cell = values[id];
    if (!cell) continue;
    // Aggregate the cash-value-support signal: the total supports cash value if
    // any contributing column does (null until a column carries the signal).
    if (cell.supportsCashValue != null) {
      supportsCashValue = (supportsCashValue ?? false) || cell.supportsCashValue;
    }
    if (cell.secondaryValue && cell.secondaryValueDescription) {
      secondarySum = (secondarySum ?? new Decimal(0)).plus(cell.secondaryValue);
      secondaryLabel = cell.secondaryValueDescription;
    }
  }
  let description: string | undefined;
  if (supportsCashValue === false) {
    // Every contributing policy is term (or otherwise cash-value-less), so the
    // total shows "--" rather than an aggregated "$0 cash value".
    description = CASH_VALUE_UNSUPPORTED_PLACEHOLDER;
  } else if (secondarySum && secondaryLabel) {
    description = `${formatCompactValue(secondarySum)} ${secondaryLabel}`;
  }
  return {
    value: sumColumnValues(values, columnIds),
    secondaryValue: secondarySum,
    secondaryValueDescription: secondaryLabel,
    supportsCashValue,
    description
  };
}

/**
 * Recalculates each section's total column to sum only the visible individual
 * entity columns, so hiding an entity column updates the displayed total.
 *
 * A section total is recomputed only when at least one of its individual
 * (non-total) columns is visible; a totals-only view filters nothing, so its
 * backend totals are kept as-is rather than collapsing to zero.
 *
 * `visibleColumns` must be the view-config-filtered columns BEFORE width-based
 * truncation — width-truncated columns still count toward the totals.
 */
export function recalculateTotalColumns(rows: RowData[], visibleColumns: BalanceSheetTable_BalanceSheetColumnFragment[]): RowData[] {
  const inEstateTotalCol = visibleColumns.find(c => c.ID === IN_ESTATE_TOTAL_COLUMN_ID);
  const outOfEstateTotalCol = visibleColumns.find(c => c.ID === OUT_OF_ESTATE_TOTAL_COLUMN_ID);
  const grandTotalCol = visibleColumns.find(c => c.ID === GRAND_TOTAL_COLUMN_ID);
  const estateTotalCol = visibleColumns.find(c => c.ID === ESTATE_TOTAL_COLUMN_ID);

  // Visible non-total column IDs grouped by their column group
  const nonTotalIdsByGroup = new Map<string, string[]>();
  for (const col of visibleColumns) {
    if (!getIsTotalColumn(col)) {
      const ids = nonTotalIdsByGroup.get(col.group) ?? [];
      ids.push(col.ID);
      nonTotalIdsByGroup.set(col.group, ids);
    }
  }
  const inEstateEntityIds = inEstateTotalCol ? nonTotalIdsByGroup.get(inEstateTotalCol.group) ?? [] : [];
  const outOfEstateEntityIds = outOfEstateTotalCol ? nonTotalIdsByGroup.get(outOfEstateTotalCol.group) ?? [] : [];

  // Only recompute a section whose individual columns are visible; otherwise it
  // isn't being filtered and the backend total must stand.
  const recalcInEstate = inEstateEntityIds.length > 0;
  const recalcOutOfEstate = outOfEstateEntityIds.length > 0;
  if (!recalcInEstate && !recalcOutOfEstate) {
    return rows;
  }
  return rows.map(row => {
    const newValues = {
      ...row.values
    };
    if (recalcInEstate) {
      newValues[IN_ESTATE_TOTAL_COLUMN_ID] = recalculateTotalCell(row.values, inEstateEntityIds);
    }
    if (recalcOutOfEstate) {
      newValues[OUT_OF_ESTATE_TOTAL_COLUMN_ID] = recalculateTotalCell(row.values, outOfEstateEntityIds);
    }

    // Keep the combined estate total in sync with the recomputed in/out totals.
    if (estateTotalCol) {
      newValues[ESTATE_TOTAL_COLUMN_ID] = recalculateTotalCell(newValues, [IN_ESTATE_TOTAL_COLUMN_ID, OUT_OF_ESTATE_TOTAL_COLUMN_ID]);
    }
    if (grandTotalCol) {
      newValues[GRAND_TOTAL_COLUMN_ID] = recalculateTotalCell(newValues, [IN_ESTATE_TOTAL_COLUMN_ID, OUT_OF_ESTATE_TOTAL_COLUMN_ID]);
    }
    return {
      ...row,
      values: newValues
    };
  });
}

// ============================================================================
// Width-based column truncation (presentation mode)
// ============================================================================

export interface WidthTruncatedColumns {
  /** Columns that fit and are rendered. Total columns are always kept. */
  renderedColumns: BalanceSheetTable_BalanceSheetColumnFragment[];
  /** Non-total columns dropped because they exceed the available width. */
  hiddenColumns: BalanceSheetTable_BalanceSheetColumnFragment[];
}

/**
 * Splits view-config-visible columns into those that fit within `maxColumns`
 * and those dropped for width. Total columns always keep a slot and are never
 * dropped; remaining capacity goes to non-total columns in order.
 */
export function computeWidthTruncatedColumns(visibleColumns: BalanceSheetTable_BalanceSheetColumnFragment[], maxColumns: number): WidthTruncatedColumns {
  if (!isFinite(maxColumns) || visibleColumns.length <= maxColumns) {
    return {
      renderedColumns: visibleColumns,
      hiddenColumns: []
    };
  }
  const totalColumnCount = visibleColumns.filter(getIsTotalColumn).length;
  const nonTotalSlots = Math.max(0, maxColumns - totalColumnCount);
  const renderedColumns: BalanceSheetTable_BalanceSheetColumnFragment[] = [];
  const hiddenColumns: BalanceSheetTable_BalanceSheetColumnFragment[] = [];
  let nonTotalRendered = 0;
  for (const column of visibleColumns) {
    if (getIsTotalColumn(column)) {
      renderedColumns.push(column);
    } else if (nonTotalRendered < nonTotalSlots) {
      renderedColumns.push(column);
      nonTotalRendered += 1;
    } else {
      hiddenColumns.push(column);
    }
  }
  return {
    renderedColumns,
    hiddenColumns
  };
}

// ============================================================================
// Row derivation — split backend NetWorth into Subtotals + Total net worth
// ============================================================================

/**
 * Returns the label for the grand-total row based on which estate types are
 * visible.  Both visible → "Total net worth"; only one → "Total in estate"
 * or "Total out of estate".
 */
export function getTotalNetWorthLabel(showInEstate: boolean, showOutOfEstate: boolean): string {
  if (showInEstate && !showOutOfEstate) return 'Total in estate';
  if (!showInEstate && showOutOfEstate) return 'Total out of estate';
  return 'Total net worth';
}
interface SplitNetWorthOptions {
  /** Which estate types are visible in the current view configuration. */
  showInEstate?: boolean;
  showOutOfEstate?: boolean;
  /** Whether the current visible columns include the rollout Grand total column. */
  hasGrandTotalColumn?: boolean;
}

/**
 * Splits the backend's single NetWorth row into two display rows:
 * 1. "Subtotals" — keeps per-column values, styled with subtotals-thick
 * 2. Grand total — value and label derived from which estate types are visible:
 *    - Both visible: sums in-estate + out-of-estate → "Total net worth"
 *    - Only in-estate: uses in-estate total → "Total in estate"
 *    - Only out-of-estate: uses out-of-estate total → "Total out of estate"
 *    - Both hidden (combined mode): uses the backend's combined total column directly
 */
export function splitNetWorthRows(rows: RowData[], {
  showInEstate = true,
  showOutOfEstate = true,
  hasGrandTotalColumn = false
}: SplitNetWorthOptions = {}): RowData[] {
  const totalLabel = getTotalNetWorthLabel(showInEstate, showOutOfEstate);
  // Show the insurance subtext only on the full net worth, not a single-estate total.
  const showInsuranceSubtext = showInEstate === showOutOfEstate;
  return rows.flatMap(row => {
    if (row.variant !== BalanceSheetRowType.NetWorth) return [row];
    const subtotalsRow: RowData = {
      ...row,
      id: `${row.id}-subtotals`,
      name: {
        lineOne: 'Subtotals',
        lineTwo: undefined
      },
      displayVariant: 'SUBTOTALS'
    };
    let grandTotal: Decimal;
    let grandTotalColumnId: string;
    if (!showInEstate && !showOutOfEstate) {
      // Combined mode: the backend returns a single combined total column.
      // Use its value directly instead of recomputing from in/out estate.
      grandTotalColumnId = ESTATE_TOTAL_COLUMN_ID;
      grandTotal = row.values[ESTATE_TOTAL_COLUMN_ID]?.value ?? new Decimal(0);
    } else {
      const inEstateTotal = row.values[IN_ESTATE_TOTAL_COLUMN_ID]?.value ?? new Decimal(0);
      const outOfEstateTotal = row.values[OUT_OF_ESTATE_TOTAL_COLUMN_ID]?.value ?? new Decimal(0);
      if (showInEstate && showOutOfEstate) {
        grandTotalColumnId = hasGrandTotalColumn ? GRAND_TOTAL_COLUMN_ID : OUT_OF_ESTATE_TOTAL_COLUMN_ID;
        grandTotal = inEstateTotal.plus(outOfEstateTotal);
      } else if (showInEstate) {
        grandTotalColumnId = IN_ESTATE_TOTAL_COLUMN_ID;
        grandTotal = inEstateTotal;
      } else {
        grandTotalColumnId = OUT_OF_ESTATE_TOTAL_COLUMN_ID;
        grandTotal = outOfEstateTotal;
      }
    }
    const totalNetWorthRow: RowData = {
      id: 'total-net-worth',
      name: {
        lineOne: totalLabel,
        lineTwo: showInsuranceSubtext ? row.name.lineTwo : undefined
      },
      path: ['total-net-worth'],
      values: {
        [grandTotalColumnId]: {
          value: grandTotal
        }
      },
      variant: BalanceSheetRowType.NetWorth,
      hasChildren: false
    };
    return [subtotalsRow, totalNetWorthRow];
  });
}

// ============================================================================
// Column sorting
// ============================================================================

export function getSortedColumns(columns: BalanceSheetTable_BalanceSheetColumnFragment[], draftColumnOrder: string[] | null): BalanceSheetTable_BalanceSheetColumnFragment[] {
  if (!draftColumnOrder) return columns;
  return [...columns].sort((a, b) => {
    if (a.ID === GRAND_TOTAL_COLUMN_ID) return 1;
    if (b.ID === GRAND_TOTAL_COLUMN_ID) return -1;
    const aIdx = draftColumnOrder.indexOf(a.ID);
    const bIdx = draftColumnOrder.indexOf(b.ID);
    const aPos = aIdx === -1 ? Infinity : aIdx;
    const bPos = bIdx === -1 ? Infinity : bIdx;
    return aPos - bPos;
  });
}

// ============================================================================
// Row structure helpers
// ============================================================================

export interface FlatRow {
  row: RowData;
  isChild: boolean;
  isLastChild: boolean;
}

/**
 * Builds a flat list of rows preserving parent-child relationship metadata.
 * Children are only included if the parent is expanded.
 */
export function buildFlatRows(allRows: RowData[], expandedSections: Set<string>): FlatRow[] {
  const result: FlatRow[] = [];
  let i = 0;
  while (i < allRows.length) {
    const currentRow = allRows[i];
    if (!currentRow) {
      i++;
      continue;
    }
    const isTopLevel = currentRow.path.length === 1;
    if (isTopLevel) {
      result.push({
        row: currentRow,
        isChild: false,
        isLastChild: false
      });
      if (currentRow.hasChildren && expandedSections.has(currentRow.id)) {
        // Collect all children (consecutive rows with path.length > 1 that belong to this parent)
        const children: RowData[] = [];
        let j = i + 1;
        while (j < allRows.length) {
          const childRow = allRows[j];
          if (!childRow || childRow.path.length <= 1) break;
          // Verify this child belongs to the current parent
          if (childRow.path[0] === currentRow.path[0]) {
            children.push(childRow);
          }
          j++;
        }
        children.forEach((child, idx) => {
          result.push({
            row: child,
            isChild: true,
            isLastChild: idx === children.length - 1
          });
        });

        // Skip past the children we just processed
        i = j;
        continue;
      }
    }
    i++;
  }
  return result;
}

// ============================================================================
// Column group helpers
// ============================================================================

export interface ColumnGroup {
  groupId: string;
  display: string;
  columns: BalanceSheetTable_BalanceSheetColumnFragment[];
}